/**
 * REDEX Stratagem Nexus - Frontend Service
 * 
 * Client for autonomous vertical discovery, arbitrage, M&A ingestion,
 * authority loops, and shadow deployments.
 */

import api from './api';

// ==========================================
// Types
// ==========================================

// Vertical Discovery Types
export interface MarketScan {
  id: string;
  vertical_slug: string;
  source: string;
  serp_data: any;
  avg_cpc: number;
  monthly_searches: number;
  competition_score: number;
  content_quality_assessment: any;
  created_at: string;
}

export interface VerticalScore {
  id: string;
  vertical_slug: string;
  economic_value: number;
  fragmentation: number;
  content_quality: number;
  compliance_friction: number;
  learning_velocity: number;
  vas_score: number;
  status: 'discovered' | 'sandbox' | 'promoted' | 'rejected';
  sandbox_start_date: string | null;
  sandbox_metrics: any;
  last_calculated: string;
}

// Arbitrage Types
export interface IntentFlow {
  id: string;
  source_market: string;
  target_market: string;
  intent_type: 'spillover' | 'seasonal' | 'regulatory';
  flow_strength: number;
  arbitrage_value: number;
  is_active: boolean;
  created_at: string;
}

export interface GeoMatrixEntry {
  id: string;
  vertical_slug: string;
  region_code: string;
  region_name: string;
  population: number;
  economic_index: number;
  compliance_complexity: number;
  entry_cost: number;
  competition_density: number;
  optimal_entry_strategy: string;
  updated_at: string;
}

export interface SeasonalPattern {
  id: string;
  vertical_slug: string;
  pattern_name: string;
  start_month: number;
  end_month: number;
  intensity_multiplier: number;
  keyword_modifiers: string[];
  prep_days_required: number;
  created_at: string;
}

// Sales Flywheel - Opportunity State Machine Types
export type OpportunityState = 
  | 'detected' 
  | 'pursued' 
  | 'outreach_sent' 
  | 'engaged' 
  | 'proposal_sent' 
  | 'contract_won' 
  | 'contract_lost';

export interface SalesOpportunity {
  id: string;
  state: OpportunityState;
  source_engine: string;
  source_id: string | null;
  title: string;
  vertical_slug: string | null;
  geo_region: string | null;
  industry_id: string | null;
  estimated_value: number | string;
  confidence_score: number | string;
  detection_rationale: Record<string, any>;
  contract_value: number | string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_company: string | null;
  recommended_channel: string | null;
  recommended_template_id: string | null;
  engagement_type: string | null;
  engagement_notes: string | null;
  loss_reason: string | null;
  competitor: string | null;
  attribution_snapshot: Record<string, any>;
  pursued_at: string | null;
  outreach_sent_at: string | null;
  engaged_at: string | null;
  proposal_sent_at: string | null;
  won_at: string | null;
  lost_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OpportunityAction {
  id: string;
  opportunity_id: string;
  action_type: string;
  from_state: OpportunityState | null;
  to_state: OpportunityState;
  actor_id: string | null;
  action_data: any;
  created_at: string;
}

export interface OutreachTemplate {
  id: string;
  template_name: string;
  category: string;
  channel: 'email' | 'sms' | 'phone';
  subject_line: string | null;
  body_template: string;
  personalization_tokens: string[];
  conversion_rate: number;
  is_active: boolean;
  created_at: string;
}

export interface OpportunityStats {
  total: number;
  by_state: Record<OpportunityState, number>;
  total_value: number;
  won_value: number;
  win_rate: number;
}

// M&A Ontology Types
export interface AcquisitionTarget {
  id: string;
  vertical_slug: string;
  domain: string;
  site_type: string;
  status: 'identified' | 'analyzing' | 'approved' | 'ingesting' | 'complete' | 'rejected';
  metrics: any;
  acquisition_score: number;
  estimated_value: number;
  ingestion_priority: number;
  contact_info: any;
  notes: string;
  created_at: string;
}

export interface OntologyIngestion {
  id: string;
  target_id: string;
  industry_id: string;
  status: 'pending' | 'crawling' | 'extracting' | 'converting' | 'merging' | 'complete' | 'failed';
  progress: number;
  pages_crawled: number;
  entities_extracted: number;
  relationships_extracted: number;
  error_log: any;
  started_at: string;
  completed_at: string | null;
}

// Authority Types
export interface LinkableAsset {
  id: string;
  industry_id: string;
  asset_type: 'data_study' | 'tool' | 'calculator' | 'infographic' | 'guide' | 'template';
  title: string;
  url: string;
  topic_cluster: string;
  creation_cost: number;
  backlinks_acquired: number;
  domain_authority_impact: number;
  is_active: boolean;
  created_at: string;
}

export interface OutreachCampaign {
  id: string;
  asset_id: string;
  campaign_name: string;
  status: 'draft' | 'active' | 'paused' | 'complete';
  target_domains: string[];
  template_id: string;
  emails_sent: number;
  responses_received: number;
  links_secured: number;
  created_at: string;
}

export interface LinkPortfolioEntry {
  id: string;
  industry_id: string;
  source_domain: string;
  source_url: string;
  target_url: string;
  anchor_text: string;
  link_type: 'dofollow' | 'nofollow' | 'ugc' | 'sponsored';
  domain_authority: number;
  traffic_value: number;
  acquisition_cost: number;
  acquisition_method: 'outreach' | 'organic' | 'paid' | 'exchange';
  status: 'prospecting' | 'outreach' | 'negotiating' | 'secured' | 'live' | 'lost' | 'retired';
  roi_score: number;
  acquired_date: string | null;
}

// Shadow Deployment Types
export interface ShadowInstance {
  id: string;
  instance_name: string;
  vertical_slug: string;
  domain: string;
  brand_config: any;
  status: 'provisioning' | 'active' | 'paused' | 'archived';
  performance_metrics: any;
  learning_contribution_score: number;
  created_at: string;
}

export interface Playbook {
  id: string;
  vertical_slug: string;
  playbook_name: string;
  version: number;
  status: 'draft' | 'testing' | 'active' | 'deprecated';
  strategy_components: any;
  success_rate: number;
  deployments_count: number;
  created_at: string;
}

export interface AnomalyAlert {
  id: string;
  instance_id: string | null;
  alert_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  detected_values: any;
  threshold_values: any;
  requires_human: boolean;
  is_acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  notes: string | null;
  created_at: string;
}

export interface CoreIntelligence {
  id: string;
  pattern_type: string;
  vertical_slug: string;
  pattern_data: any;
  confidence_score: number;
  instances_validated: number;
  last_validated: string;
  is_active: boolean;
}

// Dashboard Types
export interface StratagemDashboard {
  verticalDiscovery: {
    topVerticals: VerticalScore[];
    sandboxCount: number;
    promotedCount: number;
  };
  arbitrage: {
    activeOpportunities: number;
    totalValue: number;
    topOpportunities: any[];
  };
  acquisitions: {
    targetsIdentified: number;
    targetsAnalyzing: number;
    topTargets: AcquisitionTarget[];
  };
  shadows: {
    activeInstances: number;
    playbooksDeployed: number;
    aggregatedLearnings: number;
  };
  safeguards: {
    activeAlerts: number;
    criticalAlerts: number;
    pendingOverrides: number;
  };
}

// Market Scan Automation Result
export interface MarketScanResult {
  verticalsScanned: number;
  seasonalPatterns: number;
  intentFlows: number;
  errors: string[];
}

// Market Scan Status
export interface MarketScanStatus {
  totalVerticals: number;
  inSandbox: number;
  promoted: number;
  lastScanAt: string | null;
  intentFlows: number;
  seasonalPatterns: number;
}

// ==========================================
// API Functions
// ==========================================

class StratagemService {
  // ========== AUTOMATION ==========
  
  /**
   * Run full market scan automation to populate Vertical Discovery
   * with real industry data, seasonal patterns, and intent flows
   */
  async runMarketScan(): Promise<MarketScanResult> {
    const response = await api.post('/stratagem/automation/run-scan');
    return response.data.data;
  }

  /**
   * Get current market scan status showing populated data stats
   */
  async getMarketScanStatus(): Promise<MarketScanStatus> {
    const response = await api.get('/stratagem/automation/status');
    return response.data.data;
  }

  // Dashboard
  async getDashboard(): Promise<StratagemDashboard> {
    const response = await api.get('/stratagem/dashboard');
    return response.data.data;
  }

  // ========== VERTICAL DISCOVERY ==========
  
  async ingestMarketScan(data: Partial<MarketScan>): Promise<MarketScan> {
    const response = await api.post('/stratagem/verticals/scan', data);
    return response.data.data;
  }

  async getMarketScans(filters?: { verticalSlug?: string; source?: string; limit?: number }): Promise<MarketScan[]> {
    const response = await api.get('/stratagem/verticals/scans', { params: filters });
    return response.data.data;
  }

  async getVerticalScores(filters?: { status?: string; minVas?: number; limit?: number }): Promise<VerticalScore[]> {
    const response = await api.get('/stratagem/verticals/scores', { params: filters });
    return response.data.data;
  }

  async getTopVerticals(limit?: number): Promise<VerticalScore[]> {
    const response = await api.get('/stratagem/verticals/top', { params: { limit } });
    return response.data.data;
  }

  async promoteToSandbox(verticalSlugs: string[]): Promise<VerticalScore[]> {
    const response = await api.post('/stratagem/verticals/sandbox', { verticalSlugs });
    return response.data.data;
  }

  async runSandboxEvaluation(): Promise<any> {
    const response = await api.post('/stratagem/verticals/evaluate');
    return response.data.data;
  }

  async createIndustryFromVertical(slug: string): Promise<{ industryId: string }> {
    const response = await api.post(`/stratagem/verticals/${slug}/industry`);
    return response.data.data;
  }

  async getDiscoveryConfig(): Promise<any> {
    const response = await api.get('/stratagem/verticals/config');
    return response.data.data;
  }

  async updateDiscoveryConfig(config: any): Promise<any> {
    const response = await api.put('/stratagem/verticals/config', config);
    return response.data.data;
  }

  // ========== ARBITRAGE ==========

  async detectIntentSpillover(market: string): Promise<IntentFlow[]> {
    const response = await api.post(`/stratagem/arbitrage/detect/${market}`);
    return response.data.data;
  }

  async getActiveFlows(filters?: { sourceMarket?: string; targetMarket?: string; intentType?: string; minValue?: number }): Promise<IntentFlow[]> {
    const response = await api.get('/stratagem/arbitrage/flows', { params: filters });
    return response.data.data;
  }

  async createIntentFlow(data: Partial<IntentFlow>): Promise<IntentFlow> {
    const response = await api.post('/stratagem/arbitrage/flows', data);
    return response.data.data;
  }

  async getGeoMatrix(verticalSlug?: string): Promise<GeoMatrixEntry[]> {
    const response = await api.get('/stratagem/arbitrage/geo', { params: { verticalSlug } });
    return response.data.data;
  }

  async updateGeoMatrix(data: Partial<GeoMatrixEntry>): Promise<GeoMatrixEntry> {
    const response = await api.put('/stratagem/arbitrage/geo', data);
    return response.data.data;
  }

  async findOptimalRoute(vertical: string, region: string): Promise<GeoMatrixEntry> {
    const response = await api.get(`/stratagem/arbitrage/route/${vertical}/${region}`);
    return response.data.data;
  }

  async registerSeasonalPattern(data: Partial<SeasonalPattern>): Promise<SeasonalPattern> {
    const response = await api.post('/stratagem/arbitrage/seasonal', data);
    return response.data.data;
  }

  async getUpcomingSeasonalOpportunities(days?: number): Promise<SeasonalPattern[]> {
    const response = await api.get('/stratagem/arbitrage/seasonal/upcoming', { params: { days } });
    return response.data.data;
  }

  async getAllArbitrageOpportunities(): Promise<any[]> {
    const response = await api.get('/stratagem/arbitrage/opportunities');
    return response.data.data;
  }

  async executeArbitrage(id: string, action: any): Promise<any> {
    const response = await api.post(`/stratagem/arbitrage/execute/${id}`, { action });
    return response.data.data;
  }

  // ========== M&A ONTOLOGY INGESTION ==========

  async identifyTarget(data: Partial<AcquisitionTarget>): Promise<AcquisitionTarget> {
    const response = await api.post('/stratagem/acquisitions/targets', data);
    return response.data.data;
  }

  async getTargets(filters?: { status?: string; verticalSlug?: string; minScore?: number; limit?: number }): Promise<AcquisitionTarget[]> {
    const response = await api.get('/stratagem/acquisitions/targets', { params: filters });
    return response.data.data;
  }

  async analyzeTarget(targetId: string): Promise<AcquisitionTarget> {
    const response = await api.post(`/stratagem/acquisitions/targets/${targetId}/analyze`);
    return response.data.data;
  }

  async updateTargetStatus(targetId: string, status: string): Promise<AcquisitionTarget> {
    const response = await api.put(`/stratagem/acquisitions/targets/${targetId}/status`, { status });
    return response.data.data;
  }

  async startIngestion(targetId: string, industryId: string): Promise<OntologyIngestion> {
    const response = await api.post(`/stratagem/acquisitions/targets/${targetId}/ingest`, { industryId });
    return response.data.data;
  }

  async getIngestions(targetId?: string): Promise<OntologyIngestion[]> {
    const response = await api.get('/stratagem/acquisitions/ingestions', { params: { targetId } });
    return response.data.data;
  }

  async getIngestion(ingestionId: string): Promise<OntologyIngestion> {
    const response = await api.get(`/stratagem/acquisitions/ingestions/${ingestionId}`);
    return response.data.data;
  }

  // ========== AUTHORITY LOOP ==========

  async createLinkableAsset(data: Partial<LinkableAsset>): Promise<LinkableAsset> {
    const response = await api.post('/stratagem/authority/assets', data);
    return response.data.data;
  }

  async getAssets(industryId: string, options?: { activeOnly?: boolean; sortBy?: string }): Promise<LinkableAsset[]> {
    const response = await api.get(`/stratagem/authority/assets/${industryId}`, { params: options });
    return response.data.data;
  }

  async autoIdentifyAssets(industryId: string): Promise<LinkableAsset[]> {
    const response = await api.post(`/stratagem/authority/assets/${industryId}/auto`);
    return response.data.data;
  }

  async createCampaign(data: Partial<OutreachCampaign>): Promise<OutreachCampaign> {
    const response = await api.post('/stratagem/authority/campaigns', data);
    return response.data.data;
  }

  async getCampaigns(assetId?: string, status?: string): Promise<OutreachCampaign[]> {
    const response = await api.get('/stratagem/authority/campaigns', { params: { assetId, status } });
    return response.data.data;
  }

  async startCampaign(campaignId: string): Promise<OutreachCampaign> {
    const response = await api.post(`/stratagem/authority/campaigns/${campaignId}/start`);
    return response.data.data;
  }

  async recordOutreachResult(campaignId: string, result: any): Promise<OutreachCampaign> {
    const response = await api.post(`/stratagem/authority/campaigns/${campaignId}/result`, result);
    return response.data.data;
  }

  async addToPortfolio(data: Partial<LinkPortfolioEntry>): Promise<LinkPortfolioEntry> {
    const response = await api.post('/stratagem/authority/portfolio', data);
    return response.data.data;
  }

  async getPortfolio(industryId: string, options?: { status?: string; minRoi?: number; sortBy?: string; limit?: number }): Promise<LinkPortfolioEntry[]> {
    const response = await api.get(`/stratagem/authority/portfolio/${industryId}`, { params: options });
    return response.data.data;
  }

  async getPortfolioMetrics(industryId: string): Promise<any> {
    const response = await api.get(`/stratagem/authority/portfolio/${industryId}/metrics`);
    return response.data.data;
  }

  async retireLowImpactLinks(industryId: string, threshold?: number): Promise<{ retired: number }> {
    const response = await api.post(`/stratagem/authority/portfolio/${industryId}/retire`, { threshold });
    return response.data.data;
  }

  // ========== SHADOW DEPLOYMENTS ==========

  async createInstance(data: Partial<ShadowInstance>): Promise<ShadowInstance> {
    const response = await api.post('/stratagem/shadows/instances', data);
    return response.data.data;
  }

  async getInstances(status?: string): Promise<ShadowInstance[]> {
    const response = await api.get('/stratagem/shadows/instances', { params: { status } });
    return response.data.data;
  }

  async getInstance(instanceId: string): Promise<ShadowInstance> {
    const response = await api.get(`/stratagem/shadows/instances/${instanceId}`);
    return response.data.data;
  }

  async launchInstance(instanceId: string): Promise<ShadowInstance> {
    const response = await api.post(`/stratagem/shadows/instances/${instanceId}/launch`);
    return response.data.data;
  }

  async pauseInstance(instanceId: string): Promise<ShadowInstance> {
    const response = await api.post(`/stratagem/shadows/instances/${instanceId}/pause`);
    return response.data.data;
  }

  async getSignals(instanceId: string, limit?: number): Promise<any[]> {
    const response = await api.get(`/stratagem/shadows/instances/${instanceId}/signals`, { params: { limit } });
    return response.data.data;
  }

  async processSignals(batchSize?: number): Promise<any> {
    const response = await api.post('/stratagem/shadows/signals/process', { batchSize });
    return response.data.data;
  }

  async createPlaybook(data: Partial<Playbook>): Promise<Playbook> {
    const response = await api.post('/stratagem/playbooks', data);
    return response.data.data;
  }

  async getPlaybooks(status?: string): Promise<Playbook[]> {
    const response = await api.get('/stratagem/playbooks', { params: { status } });
    return response.data.data;
  }

  async deployPlaybook(playbookId: string, instanceId: string, customizations?: any): Promise<any> {
    const response = await api.post(`/stratagem/playbooks/${playbookId}/deploy`, { instanceId, customizations });
    return response.data.data;
  }

  async getDeployments(instanceId: string): Promise<any[]> {
    const response = await api.get(`/stratagem/shadows/instances/${instanceId}/deployments`);
    return response.data.data;
  }

  // ========== INTELLIGENCE ==========

  async getIntelligence(filters?: { patternType?: string; verticalSlug?: string; minConfidence?: number; validatedOnly?: boolean }): Promise<CoreIntelligence[]> {
    const response = await api.get('/stratagem/intelligence', { params: filters });
    return response.data.data;
  }

  async validateIntelligence(patternId: string): Promise<CoreIntelligence> {
    const response = await api.post(`/stratagem/intelligence/${patternId}/validate`);
    return response.data.data;
  }

  // ========== SALES FLYWHEEL - OPPORTUNITY STATE MACHINE ==========

  /**
   * Get all opportunities with optional filters
   */
  async getOpportunities(filters?: { 
    state?: string; 
    vertical?: string; 
    limit?: number; 
    offset?: number 
  }): Promise<SalesOpportunity[]> {
    const response = await api.get('/stratagem/opportunities', { params: filters });
    // API returns { opportunities: [...], total: n }
    return response.data.data.opportunities || response.data.data;
  }

  /**
   * Get single opportunity by ID
   */
  async getOpportunity(id: string): Promise<SalesOpportunity> {
    const response = await api.get(`/stratagem/opportunities/${id}`);
    return response.data.data;
  }

  /**
   * Get opportunity pipeline stats
   */
  async getOpportunityStats(): Promise<OpportunityStats> {
    const response = await api.get('/stratagem/opportunities/stats');
    return response.data.data;
  }

  /**
   * Get opportunity action history
   */
  async getOpportunityHistory(id: string): Promise<{ actions: OpportunityAction[] }> {
    const response = await api.get(`/stratagem/opportunities/${id}/history`);
    return response.data.data;
  }

  /**
   * Get outreach templates for opportunities
   */
  async getOutreachTemplates(category?: string): Promise<OutreachTemplate[]> {
    const response = await api.get('/stratagem/opportunities/templates', { params: { category } });
    return response.data.data;
  }

  /**
   * Detect a new opportunity (creates in DETECTED state)
   */
  async detectOpportunity(data: {
    source_type: string;
    source_id: string;
    source_data: any;
    vertical_slug?: string;
  }): Promise<{ opportunity_id: string; state: string }> {
    const response = await api.post('/stratagem/opportunities/detect', data);
    return response.data.data;
  }

  /**
   * Pursue an opportunity (DETECTED → PURSUED)
   */
  async pursueOpportunity(id: string, data?: {
    priority?: 'low' | 'medium' | 'high' | 'critical';
    notes?: string;
  }): Promise<SalesOpportunity> {
    const response = await api.post(`/stratagem/opportunities/${id}/pursue`, data);
    return response.data.data;
  }

  /**
   * Launch outreach (PURSUED → OUTREACH_SENT)
   */
  async launchOutreach(id: string, data: {
    channel: 'email' | 'sms' | 'phone' | 'linkedin';
    template_id?: string;
    custom_message?: string;
    contact_email?: string;
    contact_phone?: string;
    contact_name?: string;
  }): Promise<SalesOpportunity> {
    const response = await api.post(`/stratagem/opportunities/${id}/outreach`, data);
    return response.data.data;
  }

  /**
   * Log engagement (OUTREACH_SENT → ENGAGED)
   */
  async logEngagement(id: string, data: {
    engagement_type: 'reply' | 'call' | 'meeting' | 'demo';
    engagement_notes: string;
    next_action?: string;
    next_action_date?: string;
  }): Promise<SalesOpportunity> {
    const response = await api.post(`/stratagem/opportunities/${id}/engage`, data);
    return response.data.data;
  }

  /**
   * Send proposal (ENGAGED → PROPOSAL_SENT)
   */
  async sendProposal(id: string, data: {
    proposal_title: string;
    proposal_amount: number;
    proposal_terms?: string;
    valid_until?: string;
  }): Promise<SalesOpportunity> {
    const response = await api.post(`/stratagem/opportunities/${id}/proposal`, data);
    return response.data.data;
  }

  /**
   * Win contract (PROPOSAL_SENT → CONTRACT_WON)
   */
  async winContract(id: string, data: {
    contract_value: number;
    contract_terms?: string;
    start_date?: string;
  }): Promise<SalesOpportunity> {
    const response = await api.post(`/stratagem/opportunities/${id}/win`, data);
    return response.data.data;
  }

  /**
   * Lose contract (any state → CONTRACT_LOST)
   */
  async loseContract(id: string, data: {
    loss_reason: string;
    competitor_won?: string;
    notes?: string;
  }): Promise<SalesOpportunity> {
    const response = await api.post(`/stratagem/opportunities/${id}/lose`, data);
    return response.data.data;
  }

  // ========== VELOCITY & SAFEGUARDS ==========

  async checkVelocity(resourceType: string, industryId?: string, instanceId?: string): Promise<any> {
    const response = await api.get('/stratagem/velocity/check', { params: { resourceType, industryId, instanceId } });
    return response.data.data;
  }

  async getAlerts(filters?: { severity?: string; unacknowledgedOnly?: boolean; requiresHuman?: boolean }): Promise<AnomalyAlert[]> {
    const response = await api.get('/stratagem/alerts', { params: filters });
    return response.data.data;
  }

  async acknowledgeAlert(alertId: string, userId: string, notes?: string): Promise<AnomalyAlert> {
    const response = await api.post(`/stratagem/alerts/${alertId}/acknowledge`, { userId, notes });
    return response.data.data;
  }
}

export const stratagemService = new StratagemService();
export default stratagemService;
